import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { useAuth } from '../lib/auth.tsx';
import { BarChart, DistributionBar } from '../components/Charts.tsx';
import { FollowerGrowth } from '../components/FollowerGrowth.tsx';
import { PlatformBadge } from '../components/PlatformBadge.tsx';
import { DeltaBadge } from '../components/DeltaBadge.tsx';
import { ReadingCard } from '../components/ReadingCard.tsx';
import { RoundTable } from '../components/RoundTable.tsx';
import { useRounds } from '../lib/rounds.ts';
import { ErrorState, Loading, PageHeader } from '../components/ui.tsx';
import { integrationNotConfiguredNotice } from '../lib/integrations.ts';
import {
  PLATFORM_LABEL,
  daysBetween,
  describeAge,
  formatDate,
  todayIso,
  formatNumber,
  formatPercent,
  metricLabel,
} from '../lib/format.ts';
import type { MetricSummaryEntry, Platform } from '../../../shared/types.ts';

interface AuditMetrics {
  account_insights_30d?: {
    period_start: string;
    period_end: string;
    views_total: number;
    reach_accounts: number;
    interactions_total: number;
    accounts_engaged: number;
    profile_visits: number;
    external_link_taps: number;
    views_from_non_followers_pct?: number;
    views_from_reels_pct?: number;
  };
  follower_demographics?: {
    as_of: string;
    gender: { gender: string; followers: number; pct: number }[];
    age_range: { range: string; followers: number; pct: number }[];
    countries: { country: string; followers: number }[];
    age_25_44_combined_pct?: number;
  };
  posts_ranking_90d?: {
    period_start: string;
    period_end: string;
    top_by_reach: {
      date: string;
      type: string;
      reach: number;
      interactions: number;
      saves: number;
      shares: number;
      theme: string;
    }[];
    insight?: string;
  };
  reels_retention?: {
    period_start: string;
    period_end: string;
    sample_size_reels: number;
    avg_skip_rate_3s_pct: number;
    avg_watch_time_seconds: number;
    recommendation?: string;
    best_retention: { date: string; skip_rate_pct: number; reach: number }[];
    worst_retention: { date: string; skip_rate_pct: number; reach: number }[];
  };
  facebook_page_organic?: {
    period_start: string;
    period_end: string;
    page_fans: number;
    organic_impressions: number;
    post_engagements: number;
    video_views: number;
    comparison_to_instagram?: string;
  };
  facebook_posts_ranking?: {
    posts: { post: string; impressions: number; engagements: number; engagement_rate_pct: number }[];
    insight?: string;
  };
  facebook_ads?: {
    period_start: string;
    period_end: string;
    campaigns: {
      campaign: string;
      spend_brl: number;
      impressions: number;
      reach: number;
      clicks: number;
      objective: string;
    }[];
    note?: string;
  };
}

export function MetricsPage() {
  const { can } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const summary = useAsync(() => api.get<MetricSummaryEntry[]>('/metrics/summary'), []);
  const audit = useAsync(() => api.get<AuditMetrics>('/audit/metrics'), []);
  const rounds = useRounds();

  async function syncWindsor() {
    setSyncing(true);
    setNotice(null);
    try {
      const result = await api.post<{
        configured: boolean;
        inserted: number;
        updated: number;
        failed: string[];
      }>('/metrics/sync-windsor');
      if (!result.configured) {
        setNotice(
          integrationNotConfiguredNotice(
            'Sincronização automática',
            'WINDSOR_API_KEY',
            can('superadmin'),
          ),
        );
      } else {
        setNotice(
          result.inserted === 0 && result.updated === 0
            ? 'Nenhuma novidade desde a última sincronização.'
            : `${result.inserted} ponto(s) novo(s), ${result.updated} atualizado(s).`,
        );
        if (result.failed.length > 0) {
          setNotice((n) => `${n ?? ''} Falhas: ${result.failed.join('; ')}`);
        }
      }
      summary.reload();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Falha ao sincronizar com o Windsor AI.');
    } finally {
      setSyncing(false);
    }
  }

  if (summary.loading || audit.loading) return <Loading label="Carregando métricas…" />;
  if (summary.error) return <ErrorState message={summary.error} onRetry={summary.reload} />;
  if (audit.error) return <ErrorState message={audit.error} onRetry={audit.reload} />;

  const data = audit.data ?? {};
  const insights = data.account_insights_30d;
  const demo = data.follower_demographics;
  const liveUntil = (summary.data ?? []).reduce<string | null>(
    (max, m) => (!max || m.date > max ? m.date : max),
    null,
  );

  return (
    <>
      <PageHeader
        title="Métricas"
        subtitle={
          <>
            Instagram e Facebook.{' '}
            {liveUntil && <>Números principais coletados até {formatDate(liveUntil)} (Windsor AI). </>}
            {insights && (
              <>
                Os detalhes de 30 dias, a demografia e os rankings mais abaixo vêm da auditoria (
                {formatDate(insights.period_start)} a {formatDate(insights.period_end)}) e só mudam quando uma nova
                rodada é importada.
              </>
            )}
          </>
        }
        actions={
          can('superadmin') && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void syncWindsor()}
              disabled={syncing}
            >
              {syncing ? 'Sincronizando…' : 'Sincronizar com Windsor AI'}
            </button>
          )
        }
      />

      {notice && <p className="callout">{notice}</p>}

      <ReadingCard scope="metrics" section="metricas" title="Leitura executiva das métricas" />

      {rounds.error ? (
        <ErrorState message={rounds.error} onRetry={rounds.reload} />
      ) : (
        <>
          <RoundTable
            title="Instagram, 30 dias (Insights nativo)"
            section="metricas"
            series={rounds.seriesOf('metricas.ig_insights_30d.')}
            rounds={rounds.rounds}
            latest={rounds.latest}
            footer="O filtro 'últimos 30 dias' do Instagram não mostra as datas da janela, então as janelas de cada rodada são aproximadas e não coincidem dia a dia."
          />
          <RoundTable
            title="Retenção de reels"
            section="metricas"
            series={rounds.seriesOf('metricas.ig_reels.')}
            rounds={rounds.rounds}
            latest={rounds.latest}
            footer="Reels com alcance de pelo menos 100 contas; média simples entre reels."
          />
          <RoundTable
            title="Facebook, 30 dias"
            section="metricas"
            series={rounds.seriesOf('metricas.fb_page.')}
            rounds={rounds.rounds}
            latest={rounds.latest}
            footer="Anúncios: sem gasto novo (R$ 17,79 desde a auditoria, saldo de R$ 0,19). Demografia: a coleta não substitui a da auditoria porque a Meta atrasa esse dado e não informa a data."
          />
        </>
      )}

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Seguidores ao longo do tempo</h2>
        </div>
        <FollowerGrowth platform="instagram" />
      </section>

      {(['instagram', 'facebook'] as Platform[]).map((platform) => {
        const cards = (summary.data ?? []).filter((m) => m.platform === platform);
        if (cards.length === 0) return null;
        return (
          <section
            className="platform-group"
            key={platform}
            aria-label={`Números principais do ${PLATFORM_LABEL[platform]}`}
          >
            <header className="platform-group__header">
              <PlatformBadge platform={platform} />
              <span className="platform-group__rule" aria-hidden="true" />
              <span className="text-micro text-muted">
                {cards.length} {cards.length === 1 ? 'métrica' : 'métricas'}
              </span>
            </header>
            <div className="grid-cards">
              {cards.map((m) => (
                <div
                  className={`card stat-tile stat-tile--platform stat-tile--${m.platform}`}
                  key={`${m.platform}-${m.metric}`}
                >
                  <span className="text-label text-muted">{metricLabel(m.metric)}</span>
                  <strong className="text-hero tabular-nums">{formatNumber(m.value)}</strong>
                  <span className="text-micro text-muted">
                    Medido em {formatDate(m.date)} ({describeAge(m.date)})
                    {daysBetween(m.date, todayIso()) > 7 && (
                      <>
                        {' '}
                        <span className="badge badge-status-warning">defasado</span>
                      </>
                    )}
                  </span>
                  {daysBetween(m.date, todayIso()) <= 7 &&
                    m.deltaFromPrevious !== null &&
                    m.previousDate &&
                    m.deltaFromPrevious !== 0 && (
                    <DeltaBadge
                      delta={m.deltaFromPrevious}
                      base={m.value - m.deltaFromPrevious}
                      sinceDay={m.previousDate}
                      untilDay={m.date}
                      unit={metricLabel(m.metric).toLowerCase()}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {insights && (
        <section className="card">
          <div className="card__header">
            <h2 className="text-title">
              <PlatformBadge platform="instagram" showLabel={false} /> Desempenho do Instagram em 30
              dias
            </h2>
            <span className="text-micro text-muted">
              {formatDate(insights.period_start)} – {formatDate(insights.period_end)}
            </span>
          </div>
          <div className="kv-grid">
            <KeyValue label="Visualizações" value={formatNumber(insights.views_total)} />
            <KeyValue label="Contas alcançadas" value={formatNumber(insights.reach_accounts)} />
            <KeyValue label="Interações" value={formatNumber(insights.interactions_total)} />
            <KeyValue label="Contas engajadas" value={formatNumber(insights.accounts_engaged)} />
            <KeyValue label="Visitas ao perfil" value={formatNumber(insights.profile_visits)} />
            <KeyValue label="Toques no link" value={formatNumber(insights.external_link_taps)} />
            {insights.views_from_non_followers_pct !== undefined && (
              <KeyValue
                label="Views de não seguidores"
                value={formatPercent(insights.views_from_non_followers_pct)}
              />
            )}
            {insights.views_from_reels_pct !== undefined && (
              <KeyValue label="Views vindas de reels" value={formatPercent(insights.views_from_reels_pct)} />
            )}
          </div>
        </section>
      )}

      {demo && (
        <div className="split-2">
          <section className="card">
            <div className="card__header">
              <h2 className="text-title">Quem segue</h2>
              <span className="text-micro text-muted">Coletado em {formatDate(demo.as_of)}</span>
            </div>
            <h3 className="text-label">Gênero</h3>
            <DistributionBar
              slices={demo.gender.map((g) => ({ label: g.gender, percent: g.pct }))}
            />
            <h3 className="text-label" style={{ marginTop: 'var(--space-6)' }}>
              Faixa etária
            </h3>
            <BarChart
              data={demo.age_range.map((a) => ({
                label: a.range,
                value: a.pct,
                highlight: a.range === '25-34' || a.range === '35-44',
              }))}
              unit="%"
            />
            {demo.age_25_44_combined_pct !== undefined && (
              <p className="text-micro text-muted">
                {formatPercent(demo.age_25_44_combined_pct)} do público está entre 25 e 44 anos.
              </p>
            )}
          </section>

          <section className="card">
            <div className="card__header">
              <h2 className="text-title">Países</h2>
            </div>
            <BarChart
              data={demo.countries.map((c) => ({
                label: c.country,
                value: c.followers,
                highlight: c.country === 'Brasil',
              }))}
            />
          </section>
        </div>
      )}

      {data.posts_ranking_90d && (
        <section className="card">
          <div className="card__header">
            <h2 className="text-title">Publicações com maior alcance</h2>
            <span className="text-micro text-muted">
              {formatDate(data.posts_ranking_90d.period_start)} –{' '}
              {formatDate(data.posts_ranking_90d.period_end)}
            </span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Publicação</th>
                  <th>Tipo</th>
                  <th>Data</th>
                  <th className="align-right">Alcance</th>
                  <th className="align-right">Interações</th>
                  <th className="align-right">Salvos</th>
                  <th className="align-right">Compart.</th>
                </tr>
              </thead>
              <tbody>
                {data.posts_ranking_90d.top_by_reach.map((post) => (
                  <tr key={`${post.date}-${post.theme}`}>
                    <td>{post.theme}</td>
                    <td className="text-muted">{post.type}</td>
                    <td className="text-muted tabular-nums">{formatDate(post.date)}</td>
                    <td className="align-right tabular-nums">{formatNumber(post.reach)}</td>
                    <td className="align-right tabular-nums">{formatNumber(post.interactions)}</td>
                    <td className="align-right tabular-nums">{formatNumber(post.saves)}</td>
                    <td className="align-right tabular-nums">{formatNumber(post.shares)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.posts_ranking_90d.insight && (
            <p className="callout">{data.posts_ranking_90d.insight}</p>
          )}
        </section>
      )}

      {data.reels_retention && (
        <section className="card">
          <div className="card__header">
            <h2 className="text-title">Retenção de reels</h2>
            <span className="text-micro text-muted">
              {data.reels_retention.sample_size_reels} reels ·{' '}
              {formatDate(data.reels_retention.period_start)} –{' '}
              {formatDate(data.reels_retention.period_end)}
            </span>
          </div>
          <div className="kv-grid">
            <KeyValue
              label="Taxa média de pulo em 3s"
              value={formatPercent(data.reels_retention.avg_skip_rate_3s_pct)}
            />
            <KeyValue
              label="Tempo médio assistido"
              value={`${data.reels_retention.avg_watch_time_seconds.toFixed(1).replace('.', ',')} s`}
            />
          </div>
          <div className="split-2">
            <div>
              <h3 className="text-label">Melhor retenção</h3>
              <BarChart
                data={data.reels_retention.best_retention.map((r) => ({
                  label: formatDate(r.date),
                  value: r.skip_rate_pct,
                }))}
                unit="%"
              />
            </div>
            <div>
              <h3 className="text-label">Pior retenção</h3>
              <BarChart
                data={data.reels_retention.worst_retention.map((r) => ({
                  label: formatDate(r.date),
                  value: r.skip_rate_pct,
                }))}
                unit="%"
              />
            </div>
          </div>
          {data.reels_retention.recommendation && (
            <p className="callout">{data.reels_retention.recommendation}</p>
          )}
        </section>
      )}

      {data.facebook_page_organic && (
        <section className="card">
          <div className="card__header">
            <h2 className="text-title">
              <PlatformBadge platform="facebook" showLabel={false} /> Facebook
            </h2>
            <span className="text-micro text-muted">
              {formatDate(data.facebook_page_organic.period_start)} –{' '}
              {formatDate(data.facebook_page_organic.period_end)}
            </span>
          </div>
          <div className="kv-grid">
            <KeyValue label="Fãs da página" value={formatNumber(data.facebook_page_organic.page_fans)} />
            <KeyValue
              label="Impressões orgânicas"
              value={formatNumber(data.facebook_page_organic.organic_impressions)}
            />
            <KeyValue
              label="Engajamentos"
              value={formatNumber(data.facebook_page_organic.post_engagements)}
            />
            <KeyValue
              label="Views de vídeo"
              value={formatNumber(data.facebook_page_organic.video_views)}
            />
          </div>
          {data.facebook_posts_ranking && (
            <>
              <h3 className="text-label">Publicações com mais impressões</h3>
              <BarChart
                data={data.facebook_posts_ranking.posts.map((p) => ({
                  label: p.post,
                  value: p.impressions,
                }))}
              />
              {data.facebook_posts_ranking.insight && (
                <p className="callout">{data.facebook_posts_ranking.insight}</p>
              )}
            </>
          )}
        </section>
      )}

      {data.facebook_ads && data.facebook_ads.campaigns.length > 0 && (
        <section className="card">
          <div className="card__header">
            <h2 className="text-title">Investimento em anúncios</h2>
            <span className="text-micro text-muted">
              {formatDate(data.facebook_ads.period_start)} – {formatDate(data.facebook_ads.period_end)}
            </span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Campanha</th>
                  <th>Objetivo</th>
                  <th className="align-right">Gasto (R$)</th>
                  <th className="align-right">Impressões</th>
                  <th className="align-right">Alcance</th>
                  <th className="align-right">Cliques</th>
                </tr>
              </thead>
              <tbody>
                {data.facebook_ads.campaigns.map((c) => (
                  <tr key={c.campaign}>
                    <td>{c.campaign}</td>
                    <td className="text-muted">{c.objective}</td>
                    <td className="align-right tabular-nums">
                      {c.spend_brl.toFixed(2).replace('.', ',')}
                    </td>
                    <td className="align-right tabular-nums">{formatNumber(c.impressions)}</td>
                    <td className="align-right tabular-nums">{formatNumber(c.reach)}</td>
                    <td className="align-right tabular-nums">{formatNumber(c.clicks)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.facebook_ads.note && <p className="callout">{data.facebook_ads.note}</p>}
        </section>
      )}
    </>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="kv">
      <span className="text-micro text-muted">{label}</span>
      <strong className="text-title tabular-nums">{value}</strong>
    </div>
  );
}
